import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { trainingService, CertificateVerification as CertificateVerificationType } from '../services/trainingService';

const CertificateVerification: React.FC = () => {
  const { code: urlCode } = useParams<{ code?: string }>();
  const [code, setCode] = useState(urlCode || '');
  const [searchedCode, setSearchedCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CertificateVerificationType | null>(null);
  const [copied, setCopied] = useState(false);

  const runVerification = useCallback(async (codeToVerify: string) => {
    const trimmed = codeToVerify.trim();
    if (!trimmed) return;

    setLoading(true);
    setSearchedCode(trimmed);
    try {
      const data = await trainingService.verifyCertificate(trimmed);
      setResult(data);
    } catch {
      setResult({
        valid: false,
        message: 'The verification registry is temporarily unreachable. Please try again in a few moments.',
        certificate: null,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (urlCode && urlCode.trim()) {
      setCode(urlCode.trim());
      runVerification(urlCode.trim());
    }
  }, [urlCode, runVerification]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim()) {
      runVerification(code);
    }
  };

  const handleCopyLink = async () => {
    if (!searchedCode) return;
    const url = `${window.location.origin}/verify/${encodeURIComponent(searchedCode)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
    }
  };

  const certificate = result?.certificate;
  const isRevoked = Boolean(certificate?.revoked);
  const isValid = Boolean(result?.valid && certificate && !isRevoked);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-slate-100 flex flex-col justify-between">
      {/* Top Header */}
      <header className="border-b border-slate-700/60 bg-slate-900/80 backdrop-blur-md px-4 py-4 sm:px-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 text-white">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-purple-600 to-indigo-500 shadow-md shadow-indigo-500/20">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <div>
              <span className="text-lg font-black tracking-wider text-white">FLASH PREPARED</span>
              <span className="block text-[11px] font-medium tracking-wide text-indigo-300">
                EXECUTIVE ACADEMY REGISTRY
              </span>
            </div>
          </Link>

          <div className="flex items-center space-x-3 text-xs">
            <Link
              to="/login"
              className="rounded-lg border border-slate-600 px-3 py-1.5 font-medium text-slate-200 hover:border-slate-400 hover:text-white transition"
            >
              Sign In
            </Link>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6">
        {/* Title & Introduction */}
        <div className="text-center">
          <div className="inline-flex items-center space-x-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-3 py-1 text-xs font-semibold text-indigo-300">
            <span>Official Credential Registry</span>
            <span className="text-indigo-400">•</span>
            <span>ISO 22301 / 27001 Aligned</span>
          </div>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Verify Academy Credential
          </h1>
          <p className="mt-3 text-sm text-slate-400 max-w-xl mx-auto">
            Instant public verification for executive crisis leadership, out-of-band incident command, and tabletop readiness certificates issued by Flash Prepared.
          </p>
        </div>

        {/* Verification Form */}
        <div className="mt-8">
          <form
            onSubmit={handleSubmit}
            className="mx-auto max-w-xl rounded-2xl border border-slate-700 bg-slate-800/90 p-2 sm:p-3 shadow-2xl backdrop-blur-md"
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                  <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="e.g. FLASH-ACAD-A1B2-C3D4-E5F6"
                  className="w-full rounded-xl border border-slate-700 bg-slate-900/80 py-2.5 pl-10 pr-3 font-mono text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !code.trim()}
                className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/30 hover:from-purple-500 hover:to-indigo-500 disabled:opacity-50 transition"
              >
                {loading ? (
                  <span className="flex items-center space-x-2">
                    <svg className="h-4 w-4 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    <span>Verifying...</span>
                  </span>
                ) : (
                  <span>Verify Credential</span>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Results Area */}
        <div className="mt-8">
          {loading && (
            <div className="mx-auto max-w-xl rounded-2xl border border-slate-700/80 bg-slate-800/50 p-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-indigo-400 border-t-transparent" />
              <p className="mt-3 text-sm text-slate-300">Searching credential registry...</p>
            </div>
          )}

          {!loading && result && (
            <div className="mx-auto max-w-2xl">
              {/* VALID CERTIFICATE */}
              {isValid && certificate && (
                <div className="overflow-hidden rounded-2xl border border-emerald-500/40 bg-slate-800/90 shadow-2xl backdrop-blur-md">
                  {/* Status Banner */}
                  <div className="border-b border-emerald-500/30 bg-emerald-950/40 px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-emerald-300">Official Credential Verified</h2>
                        <p className="text-xs text-emerald-400/80">
                          Active and in good standing with Flash Prepared Executive Academy
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Body */}
                  <div className="p-6 sm:p-8 space-y-6">
                    <div>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-indigo-400">
                        Awarded To
                      </span>
                      <h3 className="mt-1 text-2xl font-black text-white">{certificate.recipient_name}</h3>
                      {certificate.company_name && (
                        <p className="text-sm font-medium text-slate-300">{certificate.company_name}</p>
                      )}
                    </div>

                    <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Certification Program
                      </div>
                      <div className="mt-1 text-base font-bold text-white">
                        Executive Crisis Leadership & Out-of-Band Incident Command
                      </div>
                      <p className="mt-1 text-xs text-slate-300">
                        Comprehensive curriculum covering ISO 22301 business continuity, ISO 27001 crisis communication governance, ransomware out-of-band escalation, and post-incident regulatory disclosure.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                      <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-3">
                        <div className="text-[10px] font-semibold uppercase text-slate-400">Score</div>
                        <div className="mt-1 text-lg font-black text-emerald-400">{certificate.average_score}%</div>
                      </div>
                      <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-3">
                        <div className="text-[10px] font-semibold uppercase text-slate-400">Modules</div>
                        <div className="mt-1 text-lg font-black text-white">
                          {certificate.modules_completed} / {certificate.total_modules}
                        </div>
                      </div>
                      <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-3">
                        <div className="text-[10px] font-semibold uppercase text-slate-400">Issue Date</div>
                        <div className="mt-1 text-sm font-bold text-white">{certificate.issue_date}</div>
                      </div>
                      <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-3">
                        <div className="text-[10px] font-semibold uppercase text-slate-400">Curriculum</div>
                        <div className="mt-1 text-sm font-bold text-indigo-300">
                          v{certificate.curriculum_version || '2026.1'}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-slate-700/80 bg-slate-900/80 px-4 py-3">
                      <div>
                        <div className="text-[10px] uppercase font-semibold text-slate-400">
                          Cryptographic Verification ID
                        </div>
                        <div className="font-mono text-xs font-bold text-indigo-300 break-all">
                          {certificate.verification_code}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleCopyLink}
                        className="inline-flex items-center space-x-1.5 rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-3 py-1.5 text-xs font-semibold text-indigo-300 hover:bg-indigo-500/20 transition self-start sm:self-auto"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        <span>{copied ? 'Copied URL!' : 'Share Link'}</span>
                      </button>
                    </div>

                    <div className="rounded-lg border border-indigo-500/20 bg-indigo-950/20 p-3 text-xs text-indigo-200">
                      <div className="font-semibold text-indigo-300">Underwriting & Compliance Recognition</div>
                      <p className="mt-0.5 text-indigo-200/80">
                        This credential contributes positively to the organization&apos;s cyber risk posture and Underwriting Resilience Score on the Flash Prepared platform.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* REVOKED CERTIFICATE */}
              {isRevoked && certificate && (
                <div className="overflow-hidden rounded-2xl border border-rose-500/40 bg-slate-800/90 shadow-2xl backdrop-blur-md">
                  <div className="border-b border-rose-500/30 bg-rose-950/40 px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-500/20 text-rose-400">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-rose-300">Credential Revoked</h2>
                        <p className="text-xs text-rose-400/80">
                          This certificate has been invalidated by the issuing authority.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    <p className="text-xs text-slate-300">
                      The credential issued to <strong className="text-white">{certificate.recipient_name}</strong> under verification code <span className="font-mono text-rose-300">{certificate.verification_code}</span> was revoked
                      {certificate.revoked_at ? ` on ${new Date(certificate.revoked_at).toLocaleDateString()}` : ''}.
                    </p>
                    <div className="rounded-xl border border-rose-900/60 bg-rose-950/20 p-4 text-xs text-rose-300">
                      Notice: This certificate is no longer valid for insurance underwriting discounts, enterprise compliance auditing, or partner verification.
                    </div>
                  </div>
                </div>
              )}

              {/* INVALID OR NOT FOUND */}
              {!result.valid && !certificate && (
                <div className="overflow-hidden rounded-2xl border border-amber-500/40 bg-slate-800/90 shadow-2xl backdrop-blur-md">
                  <div className="border-b border-amber-500/30 bg-amber-950/40 px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-400">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-amber-300">Credential Not Found</h2>
                        <p className="text-xs text-amber-400/80">No matching certificate in official registry</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-sm text-slate-300">
                      The verification code <span className="font-mono font-bold text-white">{searchedCode}</span> could not be verified.
                    </p>
                    <p className="mt-2 text-xs text-slate-400">
                      Please confirm the code was entered accurately (including uppercase letters and hyphens). If you believe this is an error, contact your enterprise administrator or Flash Prepared support.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Informational Cards */}
        <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-700/60 bg-slate-800/40 p-5">
            <div className="text-indigo-400 mb-2">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-sm font-bold text-white">Cryptographically Verifiable</h4>
            <p className="mt-1 text-xs text-slate-400">
              Each credential carries a unique collision-resistant verification token permanently recorded in the Flash Prepared registry.
            </p>
          </div>

          <div className="rounded-xl border border-slate-700/60 bg-slate-800/40 p-5">
            <div className="text-indigo-400 mb-2">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h4 className="text-sm font-bold text-white">ISO & NIST Aligned</h4>
            <p className="mt-1 text-xs text-slate-400">
              Coursework is benchmarked against ISO 22301 Clause 8.4, ISO 27001 Clause 5.30, and NIST SP 800-61 Rev 2 incident management.
            </p>
          </div>

          <div className="rounded-xl border border-slate-700/60 bg-slate-800/40 p-5">
            <div className="text-indigo-400 mb-2">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-sm font-bold text-white">Underwriting Resilience</h4>
            <p className="mt-1 text-xs text-slate-400">
              Verified certificates directly improve insurance underwriting resilience metrics, reducing cyber premium risk.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-900/60 px-4 py-6 text-center text-xs text-slate-500">
        <div className="mx-auto max-w-6xl flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} Flash Prepared. All rights reserved.</p>
          <div className="flex items-center space-x-4">
            <Link to="/privacy-policy" className="hover:text-slate-300">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-slate-300">Terms of Service</Link>
            <Link to="/about" className="hover:text-slate-300">About Flash Prepared</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CertificateVerification;
